// frontend/src/api/auth.ts
import { FASTAPI_APP_BASE_URL, WORKER_API_URL } from "./config";

/**
 * Get the current auth token from localStorage
 */
export async function getAuthToken(): Promise<string | null> {
  // AuthContext stores the token as 'token', not 'access_token'
  return localStorage.getItem('token');
}

/**
 * Login a user and retrieve JWT tokens
 */
export async function loginUser(usernameOrEmail: string, password: string): Promise<any> {
  console.log("Login request details:", {
    url: `${WORKER_API_URL}/auth/token`,
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: { username: usernameOrEmail, password: "********" },
  });

  // Backend expects OAuth2PasswordRequestForm (x-www-form-urlencoded)
  const form = new URLSearchParams();
  form.set("username", usernameOrEmail);
  form.set("password", password);

  const response = await fetch(`${WORKER_API_URL}/auth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: form.toString(),
  });

  console.log("Login response status:", response.status);
  console.log("Login response headers:", response.headers);

  const data = await response.json();
  console.log("Login response data:", data);

  if (!response.ok) {
    console.error("Login failed:", {
      status: response.status,
      statusText: response.statusText,
      data: data,
    });
    throw new Error(data.error || "Login failed");
  }
  return data;
}

/**
 * Register a new user
 */
export async function registerUser(
  email: string,
  username: string,
  password: string
): Promise<any> {
  console.log("Registration request details:", {
    url: `${WORKER_API_URL}/auth/register`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: { email, username, password },
  });

  const response = await fetch(`${WORKER_API_URL}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, username, password }),
  });

  console.log("Registration response status:", response.status);
  console.log("Registration response headers:", response.headers);

  const data = await response.json();
  console.log("Registration response data:", data);

  if (!response.ok) {
    console.error("Registration failed:", {
      status: response.status,
      statusText: response.statusText,
      data: data,
    });

    // Handle structured error response
    if (data.error && data.field) {
      // Special case for email already registered
      if (data.error === "Email already registered" && data.field === "email") {
        throw new Error("This email is already registered. Please use a different email or try logging in.");
      }
      throw new Error(`${data.error} (${data.field})`);
    } else if (data.error) {
      throw new Error(data.error);
    } else {
      throw new Error("Registration failed");
    }
  }
  return data;
}

/**
 * Logout a user (blacklist token)
 */
export async function logoutUser(token: string): Promise<void> {
  // No explicit backend logout endpoint; tokens are stateless.
  // Intentionally no-op beyond client-side state clearing.
  return;
}

/**
 * Refresh the authentication token
 */
export async function refreshUserToken(token: string): Promise<any> {
  if (!token) {
    throw new Error("No token provided");
  }
  const response = await fetch(`${WORKER_API_URL}/auth/refresh`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    // body not required for refresh; keep empty
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Token refresh failed");
  }
  return data;
}

/**
 * Get user profile
 */
export async function getUserProfile(
  token: string,
  bypassCache: boolean = false
): Promise<any> {
  if (!token) {
    throw new Error("No token provided");
  }
  const url = new URL(`${WORKER_API_URL}/auth/me`);
  if (bypassCache) url.searchParams.set("bypassCache", "true");
  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    try {
      const err = await response.json();
      throw new Error(
        err.error || `Failed to fetch user profile: ${response.status}`
      );
    } catch {
      const text = await response.text();
      throw new Error(
        `Failed to fetch user profile: ${response.status} - ${text}`
      );
    }
  }
  const data = await response.json();
  return data;
}

/**
 * Verify email with 6-digit code
 */
export async function verifyEmail(email: string, code: string): Promise<any> {
  const url = `${WORKER_API_URL}/auth/verify-email`;
    
  console.log("Email verification request details:", {
    url: url,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: { email, code },
  });

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });

  console.log("Email verification response status:", response.status);
  const data = await response.json();
  console.log("Email verification response data:", data);

  if (!response.ok) {
    console.error("Email verification failed:", {
      status: response.status,
      statusText: response.statusText,
      data: data,
    });
    // Handle both Worker API and FastAPI error formats
    throw new Error(data.error || data.detail || "Email verification failed");
  }
  return data;
}

/**
 * Resend verification email
 */
export async function resendVerificationEmail(email: string): Promise<any> {
  const url = `${WORKER_API_URL}/auth/resend-verification`;
    
  console.log("Resend verification request details:", {
    url: url,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: { email },
  });

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  console.log("Resend verification response status:", response.status);
  const data = await response.json();
  console.log("Resend verification response data:", data);

  if (!response.ok) {
    console.error("Resend verification failed:", {
      status: response.status,
      statusText: response.statusText,
      data: data,
    });
    // Handle both Worker API and FastAPI error formats
    throw new Error(data.error || data.detail || "Failed to resend verification email");
  }
  return data;
}

/**
 * Check user's verification and onboarding status
 */
export async function checkUserStatus(email: string): Promise<{ email_verified: boolean; profile_exists: boolean }> {
  const response = await fetch(`${WORKER_API_URL}/auth/check-user-status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.detail || 'Failed to check user status');
  }

  return data;
}

/**
 * Parse a JWT token's payload
 */
export function parseToken(token: string): any {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error parsing token:", error);
    return null;
  }
}

/**
 * Get token expiration time (ms)
 */
export function getTokenExpiry(token: string): number | null {
  const payload = parseToken(token);
  if (!payload) return null;
  const expRaw = payload.exp;
  if (typeof expRaw === 'number') {
    // Heuristic: if value looks like seconds (10 digits), convert to ms; if already ms (13+ digits), use as-is
    if (expRaw < 1e12) return expRaw * 1000;
    return expRaw;
  }
  if (typeof expRaw === 'string') {
    const num = Number(expRaw);
    if (!Number.isNaN(num) && num > 0) {
      return num < 1e12 ? num * 1000 : num;
    }
  }
  return null;
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const expiry = getTokenExpiry(token);
  if (!expiry) {
    // If we cannot determine expiry, do not eagerly expire on the client.
    // Let the server enforce via 401 and interceptors handle logout.
    return false;
  }
  // Small negative leeway to reduce false positives due to skew
  const SKEW_MS = 15 * 1000;
  return Date.now() >= (expiry - SKEW_MS);
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  token: string,
  userData: any
): Promise<any> {
  if (!token) {
    throw new Error("No token provided");
  }

  const response = await fetch(`${WORKER_API_URL}/profile/update`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    try {
      const err = await response.json();
      throw new Error(
        err.error || `Failed to update user profile: ${response.status}`
      );
    } catch (error) {
      // Avoid reading body stream twice error
      if (
        error instanceof TypeError &&
        error.message.includes("body stream already read")
      ) {
        throw new Error(`Failed to update user profile: ${response.status}`);
      }
      throw error;
    }
  }

  const data = await response.json();
  return data;
}

/**
 * Request a password reset email
 */
export async function requestPasswordReset(email: string): Promise<any> {
  const response = await fetch(`${WORKER_API_URL}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();
  
  // Always return success to prevent user enumeration
  return data;
}

/**
 * Reset password with a token
 */
export async function resetPassword(
  email: string,
  code: string,
  newPassword: string,
  confirmPassword?: string
): Promise<any> {
  const response = await fetch(`${WORKER_API_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      email,
      code,
      new_password: newPassword,
      ...(confirmPassword ? { confirm_password: confirmPassword } : {})
    }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || "Failed to reset password");
  }

  const data = await response.json();
  return data;
}

// Export all auth service functions
export const authService = {
  loginUser,
  registerUser,
  verifyEmail,
  resendVerificationEmail,
  updateUserProfile,
  getUserProfile,
  requestPasswordReset,
  resetPassword,
  getAuthToken,
  logoutUser,
  refreshUserToken,
};
